import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useEmpresa } from '../../context/EmpresaContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { notificacionService } from '../../services/notificacion.service';
import {
  SunIcon,
  MoonIcon,
  BellIcon,
  ChevronDownIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  CommandLineIcon,
} from '@heroicons/react/24/outline';


interface INotificacion {
  id: number;
  usuarioId: number;
  empresaId: number | null;
  titulo: string;
  mensaje: string;
  tipo: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  link: string | null;
  leida: boolean;
  leidaAt: string | null;
  createdAt: string;
}

export const Header: React.FC = () => {
  const { user } = useAuth();
  const { empresaActual, empresas, setEmpresaActual } = useEmpresa();
  const { setTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [showEmpresaMenu, setShowEmpresaMenu] = useState(false);
  const [showNotifMenu, setShowNotifMenu] = useState(false);
  const [notificaciones, setNotificaciones] = useState<INotificacion[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const allPages = [
    { path: '/dashboard', label: 'Dashboard', category: 'Principal' },
    { path: '/actividad', label: 'Actividad', category: 'Principal' },
    { path: '/empresas', label: 'Empresas', category: 'Empresa' },
    { path: '/departamentos', label: 'Departamentos', category: 'Empresa' },
    { path: '/cargos', label: 'Cargos', category: 'Empresa' },
    { path: '/empleados', label: 'Empleados', category: 'Empleados' },
    { path: '/usuarios', label: 'Usuarios', category: 'Empleados' },
    { path: '/tipos-contrato', label: 'Tipos Contrato', category: 'Empleados' },
    { path: '/perfiles', label: 'Perfiles', category: 'Empleados' },
    { path: '/asistencias', label: 'Asistencias', category: 'Asistencias' },
    { path: '/horarios', label: 'Horarios', category: 'Asistencias' },
    { path: '/turnos', label: 'Turnos', category: 'Asistencias' },
    { path: '/rotaciones', label: 'Rotaciones', category: 'Asistencias' },
    { path: '/calendarios', label: 'Calendarios', category: 'Asistencias' },
    { path: '/nomina', label: 'Nómina', category: 'Nómina' },
    { path: '/reglas', label: 'Reglas de nómina', category: 'Nómina' },
    { path: '/adelantos', label: 'Adelantos', category: 'Nómina' },
    { path: '/prestamos', label: 'Préstamos', category: 'Nómina' },
    { path: '/premios', label: 'Premios', category: 'Desempeño' },
    { path: '/sanciones', label: 'Sanciones', category: 'Desempeño' },
    { path: '/reportes', label: 'Reportes', category: 'Reportes' },
    { path: '/dispositivos', label: 'Dispositivos', category: 'Sistema' },
    { path: '/configuracion', label: 'Configuración', category: 'Sistema' },
    { path: '/ayuda', label: 'Ayuda', category: 'Sistema' },
  ];

  // Tabla de alias para búsqueda rápida (de la documentación)
  const aliasMap: { [alias: string]: string } = {
    // Tema/Colores/Interfaz
    tema: '/configuracion',
    color: '/configuracion',
    interfaz: '/configuracion',
    oscuro: '/configuracion',
    claro: '/configuracion',
    apariencia: '/configuracion',
    modo: '/configuracion',
    personalizar: '/configuracion',
    // Logo
    logo: '/configuracion',
    imagen: '/configuracion',
    marca: '/configuracion',
    encabezado: '/configuracion',
    // Empleado
    empleado: '/empleados',
    nuevo: '/empleados',
    alta: '/empleados',
    personal: '/empleados',
    contratar: '/empleados',
    añadir: '/empleados',
    // Horario partido
    'horario partido': '/horarios',
    pausa: '/horarios',
    almuerzo: '/horarios',
    'dos turnos': '/horarios',
    'jornada partida': '/horarios',
    // Horario secundario
    'horario secundario': '/turnos',
    sábado: '/turnos',
    'dia especial': '/turnos',
    'segundo horario': '/turnos',
    // Rotación
    rotación: '/rotaciones',
    'turnos rotativos': '/rotaciones',
    ciclo: '/rotaciones',
    '24/7': '/rotaciones',
    patrón: '/rotaciones',
    secuencia: '/rotaciones',
    // Asistencia
    asistencia: '/asistencias',
    fichada: '/asistencias',
    entrada: '/asistencias',
    salida: '/asistencias',
    marcar: '/asistencias',
    checar: '/asistencias',
    registro: '/asistencias',
    // Nómina
    nómina: '/nomina',
    calcular: '/nomina',
    pago: '/nomina',
    sueldo: '/nomina',
    liquidar: '/nomina',
    planilla: '/nomina',
    // Política
    política: '/reglas',
    'horas extra': '/reglas',
    regla: '/reglas',
    bonificación: '/reglas',
    deducción: '/reglas',
    recargo: '/reglas',
    // Dispositivo
    dispositivo: '/dispositivos',
    hikvision: '/dispositivos',
    biométrico: '/dispositivos',
    lector: '/dispositivos',
    reloj: '/dispositivos',
    terminal: '/dispositivos',
    // Reporte
    reporte: '/reportes',
    informe: '/reportes',
    exportar: '/reportes',
    pdf: '/reportes',
    excel: '/reportes',
    estadística: '/reportes',
    // Contraseña
    contraseña: '/perfil',
    password: '/perfil',
    seguridad: '/perfil',
    clave: '/perfil',
    // Empresa
    empresa: '/configuracion',
    'datos empresa': '/configuracion',
    información: '/configuracion',
    'razón social': '/configuracion',
    // Banco de horas
    'banco de horas': '/empleados',
    'horas acumuladas': '/empleados',
    'saldo horas': '/empleados',
    // Vacaciones
    vacaciones: '/empleados',
    permiso: '/empleados',
    ausencia: '/empleados',
    licencia: '/empleados',
    // Historial
    historial: '/empleados',
    cambios: '/empleados',
    movimientos: '/empleados',
    trayectoria: '/empleados',
    // Tolerancia
    tolerancia: '/horarios',
    'minutos de retraso': '/horarios',
    'permiso de llegada': '/horarios',
    // Turno nocturno
    'turno nocturno': '/horarios',
    noche: '/horarios',
    'horario nocturno': '/horarios',
    'jornada nocturna': '/horarios',
    // Ayuda
    ayuda: '/ayuda',
    soporte: '/ayuda',
    manual: '/ayuda',
    faq: '/ayuda',
    preguntas: '/ayuda',
  };

  // Buscar alias directo
  const getAliasPath = (query: string): string | undefined => {
    const normalized = query.trim().toLowerCase();
    // Buscar coincidencia exacta
    if (aliasMap[normalized]) return aliasMap[normalized];
    // Buscar coincidencia parcial (palabra incluida)
    const found = Object.keys(aliasMap).find((alias) =>
      normalized.includes(alias),
    );
    return found ? aliasMap[found] : undefined;
  };

  const filteredPages = searchQuery.trim()
    ? (() => {
        // Si hay alias, mostrar solo esa página
        const aliasPath = getAliasPath(searchQuery);
        if (aliasPath) {
          const page = allPages.find((p) => p.path === aliasPath);
          return page ? [page] : [];
        }
        // Si no, buscar por label/categoría
        return allPages.filter(
          (p) =>
            p.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.category.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      })()
    : [];

  // Keyboard shortcut Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowSearchResults(false);
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearchNavigation = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
    searchInputRef.current?.blur();
  };

  const handleKeyDownSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredPages.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(
        (prev) => (prev - 1 + filteredPages.length) % filteredPages.length,
      );
    } else if (e.key === 'Enter' && filteredPages[selectedIndex]) {
      handleSearchNavigation(filteredPages[selectedIndex].path);
    }
  };

  // Close search on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (!showNotifMenu) return;
    const fetchNotifs = async () => {
      setNotifLoading(true);
      try {
        const res = await notificacionService.getAll();
        setNotificaciones(res.data || []);
      } catch {
        setNotificaciones([]);
      } finally {
        setNotifLoading(false);
      }
    };
    fetchNotifs();
  }, [showNotifMenu]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifMenu(false);
      }
    };
    if (showNotifMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifMenu]);

  // Poll for unread count every 60s
  const [noLeidasCount, setNoLeidasCount] = useState(0);
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await notificacionService.getAll();
        const todas: INotificacion[] = res.data || [];
        setNoLeidasCount(todas.filter((n) => !n.leida).length);
      } catch {
        /* ignore */
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [empresaActual]);

  const handleMarcarLeida = async (id: number) => {
    try {
      await notificacionService.marcarLeida(id);
      setNotificaciones((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, leida: true, leidaAt: new Date().toISOString() }
            : n,
        ),
      );
      setNoLeidasCount((c) => Math.max(0, c - 1));
    } catch {
      /* ignore */
    }
  };

  const handleMarcarTodasLeidas = async () => {
    try {
      await notificacionService.marcarTodasLeidas();
      setNotificaciones((prev) =>
        prev.map((n) => ({
          ...n,
          leida: true,
          leidaAt: new Date().toISOString(),
        })),
      );
      setNoLeidasCount(0);
    } catch {
      /* ignore */
    }
  };

  const handleVerDetalle = (notif: INotificacion) => {
    if (notif.link) navigate(notif.link);
    if (!notif.leida) handleMarcarLeida(notif.id);
    setShowNotifMenu(false);
  };

  const handleCambiarEmpresa = (empresaId: number) => {
    const empresa = empresas.find((e) => e.id === empresaId);
    if (empresa) {
      setEmpresaActual(empresa);
      setShowEmpresaMenu(false);
    }
  };

  return (
    <header
      className="h-16 backdrop-blur-md border-b flex items-center justify-between px-4 md:px-6 sticky top-0 z-30"
      style={{
        backgroundColor: isDark
          ? 'rgba(10, 14, 39, 0.8)'
          : 'rgba(245, 247, 255, 0.8)',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgb(229,231,235)',
      }}
    >
      {/* Empresa info con selector */}
      <div className="flex items-center gap-3">
        {empresaActual?.logoUrl ? (
          <img
            src={
              empresaActual.logoUrl.startsWith('http')
                ? empresaActual.logoUrl
                : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '')}${empresaActual.logoUrl}`
            }            
            alt="Logo"
            className="h-8 w-auto"
          />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <span className="text-white font-bold text-sm">N</span>
          </div>
        )}

        {/* Selector de empresa */}
        <div className="relative">
          <button
            onClick={() => setShowEmpresaMenu(!showEmpresaMenu)}
            className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-white/5 p-1 rounded-lg transition-colors"
          >
            <div>
              <p
                className="font-medium text-sm"
                style={{
                  color: isDark
                    ? 'var(--color-text-dark, #e8eaf6)'
                    : 'var(--color-text-light, #1a237e)',
                }}
              >
                {empresaActual?.nombre || 'Seleccionar Empresa'}
              </p>
              <p className="text-gray-500 text-xs">
                {empresaActual?.pais || ''}
              </p>
            </div>
            {empresas.length > 1 && (
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            )}
          </button>

          {/* Dropdown de empresas */}
          {showEmpresaMenu && empresas.length > 1 && (
            <div
              className="absolute top-full left-0 mt-2 w-64 border rounded-xl shadow-lg z-50 overflow-hidden"
              style={{
                backgroundColor: isDark
                  ? 'var(--color-surface-dark, #141832)'
                  : 'var(--color-surface-light, #ffffff)',
                borderColor: isDark
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgb(229,231,235)',
              }}
            >
              <div
                className="p-2 border-b"
                style={{
                  borderColor: isDark
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgb(229,231,235)',
                }}
              >
                <p className="text-gray-500 dark:text-gray-400 text-xs px-2">
                  Cambiar empresa
                </p>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {empresas.map((empresa) => (
                  <button
                    key={empresa.id}
                    onClick={() => handleCambiarEmpresa(empresa.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors ${
                      empresaActual?.id === empresa.id ? 'bg-primary/20' : ''
                    }`}
                  >
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                    <div className="text-left">
                      <p
                        className="text-sm font-medium"
                        style={{
                          color: isDark
                            ? 'var(--color-text-dark, #e8eaf6)'
                            : 'var(--color-text-light, #1a237e)',
                        }}
                      >
                        {empresa.nombre}
                      </p>
                      <p className="text-gray-500 text-xs">{empresa.pais}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Search Bar */}
        <div className="relative mr-2 hidden md:block" ref={searchRef}>
          <div className="relative group">
            <div
              className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors ${showSearchResults ? 'text-primary' : 'text-gray-400 group-focus-within:text-primary'}`}
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar página... (Ctrl+K)"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
                setSelectedIndex(0);
              }}
              onFocus={() => setShowSearchResults(true)}
              onKeyDown={handleKeyDownSearch}
              className="pl-9 pr-12 py-1.5 w-64 rounded-xl text-sm border focus:outline-none transition-all duration-300 focus:w-80"
              style={{
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
                borderColor: isDark
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgb(229,231,235)',
                color: isDark ? '#e8eaf6' : '#1a237e',
                boxShadow: showSearchResults
                  ? '0 0 0 2px rgba(99, 102, 241, 0.2)'
                  : 'none',
              }}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-[10px] font-bold text-gray-500 border rounded px-1.5 py-0.5 opacity-40">
                Ctrl+K
              </span>
            </div>
          </div>

          {/* Search Results Dropdown */}
          {showSearchResults && filteredPages.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 mt-2 rounded-2xl shadow-2xl border z-50 overflow-hidden backdrop-blur-xl"
              style={{
                backgroundColor: isDark
                  ? 'rgba(20, 24, 50, 0.95)'
                  : 'rgba(255, 255, 255, 0.95)',
                borderColor: isDark
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgb(229,231,235)',
              }}
            >
              <div className="max-h-80 overflow-y-auto py-2">
                {filteredPages.map((page, index) => (
                  <button
                    key={page.path}
                    onClick={() => handleSearchNavigation(page.path)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 transition-colors ${
                      selectedIndex === index
                        ? 'bg-primary/10 text-primary'
                        : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <CommandLineIcon
                        className={`w-4 h-4 ${selectedIndex === index ? 'text-primary' : 'text-gray-500'}`}
                      />
                      <div className="text-left">
                        <p
                          className={`text-sm font-medium ${selectedIndex === index ? 'text-primary' : isDark ? '#e8eaf6' : '#1a237e'}`}
                        >
                          {page.label}
                        </p>
                        <p className="text-[10px] opacity-60 uppercase tracking-widest">
                          {page.category}
                        </p>
                      </div>
                    </div>
                    {selectedIndex === index && (
                      <span className="text-[10px] font-bold bg-primary/20 px-1.5 py-0.5 rounded text-primary">
                        ENTER ↵
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Theme toggle */}
        <button
          onClick={() => setTheme(isDark ? 'claro' : 'oscuro')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          {isDark ? (
            <SunIcon className="w-5 h-5" />
          ) : (
            <MoonIcon className="w-5 h-5" />
          )}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => {
              setShowNotifMenu(!showNotifMenu);
              setShowEmpresaMenu(false);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors relative"
          >
            <BellIcon className="w-5 h-5" />
            {noLeidasCount > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 rounded-full text-[9px] font-bold text-white">
                {noLeidasCount > 99 ? '99+' : noLeidasCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifMenu && (
            <div
              className="absolute top-full right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] border rounded-xl shadow-2xl z-50 overflow-hidden"
              style={{
                backgroundColor: isDark ? '#141832' : '#ffffff',
                borderColor: isDark
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgb(229,231,235)',
              }}
            >
              {/* Header */}
              <div
                className="px-4 py-3 border-b flex items-center justify-between"
                style={{
                  borderColor: isDark
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgb(229,231,235)',
                }}
              >
                <div className="flex items-center gap-2">
                  <p
                    className="text-sm font-semibold"
                    style={{ color: isDark ? '#e8eaf6' : '#1a237e' }}
                  >
                    Notificaciones
                  </p>
                  {noLeidasCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400">
                      {noLeidasCount}
                    </span>
                  )}
                </div>
                {noLeidasCount > 0 && (
                  <button
                    onClick={handleMarcarTodasLeidas}
                    className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Marcar todas leídas
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="max-h-[400px] overflow-y-auto">
                {notifLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400 mr-2" />
                    <span className="text-sm text-gray-400">Cargando...</span>
                  </div>
                ) : notificaciones.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <CheckCircleIcon className="w-8 h-8 text-green-500/30 mb-2" />
                    <p className="text-xs text-gray-500">
                      No hay notificaciones
                    </p>
                  </div>
                ) : (
                  notificaciones.slice(0, 20).map((notif) => {
                    const isWarning = notif.tipo === 'WARNING';
                    const iconColor = isWarning
                      ? notif.leida
                        ? 'text-gray-500'
                        : 'text-amber-400'
                      : notif.leida
                        ? 'text-gray-500'
                        : 'text-blue-400';
                    return (
                      <div
                        key={notif.id}
                        className={`flex items-start gap-2.5 px-4 py-3 border-b transition-colors cursor-pointer hover:bg-white/5 ${
                          notif.leida ? 'opacity-50' : ''
                        }`}
                        style={{
                          borderColor: isDark
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.05)',
                          backgroundColor:
                            !notif.leida && isWarning
                              ? isDark
                                ? 'rgba(245,158,11,0.04)'
                                : 'rgba(245,158,11,0.03)'
                              : undefined,
                        }}
                        onClick={() => handleVerDetalle(notif)}
                      >
                        <ExclamationTriangleIcon
                          className={`w-4 h-4 shrink-0 mt-0.5 ${iconColor}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-xs font-medium truncate"
                            style={{ color: isDark ? '#e8eaf6' : '#1a237e' }}
                          >
                            {notif.titulo.replace('⚠️ ', '')}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">
                            {notif.mensaje}
                          </p>
                          <p className="text-[10px] text-gray-600 mt-1">
                            {new Date(notif.createdAt).toLocaleString('es-DO', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                        {!notif.leida && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarcarLeida(notif.id);
                            }}
                            className="p-1 rounded hover:bg-green-500/10 text-gray-400 hover:text-green-400 transition-colors shrink-0"
                            title="Marcar como leída"
                          >
                            <CheckIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* User avatar */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center ml-2">
          <span className="text-white font-bold text-sm">
            {user?.nombre?.charAt(0) || 'U'}
          </span>
        </div>
      </div>
    </header>
  );
};
